import React from "react"
import type { TFunction } from "i18next"
import type { FormDescriptionImage } from "@f2e/shared/types"
import { FieldDescription } from "@f2e/ui/components/InteractionRequestForm"

import { Label, Input } from "@f2e/ui/components/base"

import { OptionsEditor } from "./OptionsEditor"
import { BaseFieldEditor, BaseFieldProps } from "./BaseFieldEditor"

export type CheckboxField = BaseFieldProps<{
  type: "checkbox"
  name: string
  question: string
  label: string
  placeholder: string
  required: boolean
  options?: { label: string; value: string }[]
}>

export const CheckboxFieldEditor: React.FC<CheckboxField> = ({
  field,
  onChange,
  disabled,
}) => {
  return (
    <>
      <BaseFieldEditor
        field={field}
        onChange={onChange}
        disabled={disabled}
        showPlaceholder={false}
      />
      <OptionsEditor field={field} onChange={onChange} disabled={disabled} />
    </>
  )
}

export default CheckboxFieldEditor

export function validateCheckboxField(
  field: { name: string; required?: boolean },
  value: unknown,
  t: TFunction
): string | undefined {
  if (!field.required) return undefined
  const arr = Array.isArray(value) ? value : []
  if (arr.length === 0) {
    return t(
      "workspace.agent.basicForm.formBuilder.validation.checkboxRequired"
    )
  }
  return undefined
}

export const CheckboxFieldRenderer: React.FC<{
  field: {
    id?: string
    name?: string
    question: string
    placeholder: string
    options?: { label: string; value: string }[]
    required: boolean
    helpText?: string
    descriptionImage?: FormDescriptionImage
  }
  mode: "preview" | "runtime"
  value?: string[]
  onChange?: (v: string[]) => void
  disabled?: boolean
  error?: string
  contextId?: string // Unique context identifier to prevent React from reusing component instances
}> = ({ field, mode, value, onChange, disabled, error, contextId }) => {
  const opts = field.options || []
  const isPreview = mode === "preview"
  const [local, setLocal] = React.useState<string[]>([])
  const selected = isPreview ? local : value || []
  // Use field id or name to make checkbox ids unique per field instance
  const fieldId = field.id || field.name || "checkbox-field"
  const uniqueId = `${contextId || "default"}-${fieldId}`

  const toggle = (v: string) => {
    const next = selected.includes(v)
      ? selected.filter((x) => x !== v)
      : [...selected, v]
    if (isPreview) {
      setLocal(next)
    } else {
      onChange?.(next)
    }
  }
  return (
    <div className="space-y-1">
      <Label className="text-xs">
        {field.question}{" "}
        {field.required ? <span className="text-red-500">*</span> : null}
      </Label>
      <FieldDescription
        helpText={field.helpText}
        descriptionImage={field.descriptionImage}
      />
      {opts.length > 0 ? (
        <div
          className={`space-y-2 ${error ? "rounded-md border border-red-500 p-2" : ""}`}
        >
          {opts.map((o, idx) => {
            const id = `checkbox-${uniqueId}-${o?.value || idx}`
            const checked = selected.includes(o?.value)
            return (
              <label
                key={id}
                htmlFor={id}
                className="flex items-center gap-2 text-sm"
              >
                <input
                  id={id}
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300"
                  checked={checked}
                  onChange={() => toggle(o.value)}
                  disabled={disabled}
                />
                <span>{o.label}</span>
              </label>
            )
          })}
        </div>
      ) : (
        <Input
          placeholder={field.placeholder}
          disabled={disabled}
          className={
            error ? "border-red-500 focus-visible:ring-red-500" : undefined
          }
        />
      )}
      {error ? <div className="text-xs text-red-500">{error}</div> : null}
    </div>
  )
}

export function serializeCheckboxField(
  field: { options?: { label: string; value: string }[] },
  raw: unknown
): unknown {
  const arr = Array.isArray(raw) ? raw : []
  if (arr.length === 0) return undefined
  const options = Array.isArray(field.options) ? field.options : []
  const mapped = arr.map((v) => {
    const found = options.find((o) => o.value === v)
    return found ? found.label : v
  })
  return mapped.length > 0 ? mapped : undefined
}
